import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { DashboardLayout } from "components/DashboardLayout";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { useToast } from "hooks/useToast";
import {
  useGetMcpConnectionUrlQuery,
  useGenerateMcpConnectionUrlMutation,
  useGetAdminMcpConnectionUrlQuery,
  useGenerateAdminMcpConnectionUrlMutation,
} from "store/apis/mcpConnection";
import { CopyIcon } from "components/icons/CopyIcon";
import { ReactNode } from "react";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";

const Step = ({
  number,
  title,
  description,
}: {
  number: number;
  title: string;
  description: ReactNode;
}) => (
  <div className="t-flex t-gap-3">
    <div className="t-flex t-h-6 t-w-6 t-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-purple-10 t-text-body-sm t-font-semibold t-text-purple">
      {number}
    </div>
    <div className="t-flex t-gap-1 t-flex-col t-min-w-0 t-w-full">
      <div className="t-text-body-sm t-font-semibold t-text-text-60">
        {title}
      </div>
      <div className="t-text-body-sm t-text-text-30">{description}</div>
    </div>
  </div>
);

const CopyUrlButton = ({ url }: { url: string }) => {
  const { successToast } = useToast();
  return (
    <Button
      customType="secondary"
      size="small"
      block
      rightIcon={<CopyIcon />}
      onClick={() => {
        navigator.clipboard.writeText(url);
        successToast({
          title: "Url copied",
          message: "Generated url copied to clipboard",
        });
      }}
    >
      <span className="t-truncate t-w-[82%]">{url}</span>
    </Button>
  );
};

const CustomerConnectionUrl = () => {
  const { alertToast } = useToast();
  const { data, isLoading } = useGetMcpConnectionUrlQuery();
  const [generateUrl, { isLoading: isGenerating }] =
    useGenerateMcpConnectionUrlMutation();

  const handleGenerate = async () => {
    try {
      await generateUrl().unwrap();
    } catch {
      alertToast({ message: "Failed to generate connection URL" });
    }
  };

  if (isLoading) {
    return <Skeleton className="t-h-7 t-w-full" />;
  }

  if (data?.generated && data?.mcp_url) {
    return <CopyUrlButton url={data.mcp_url} />;
  }

  return (
    <div>
      <Button
        customType="primary"
        size="small"
        onClick={handleGenerate}
        isLoading={isGenerating}
        disabled={isGenerating}
      >
        Generate Connection URL
      </Button>
    </div>
  );
};

const AdminConnectionUrl = () => {
  const { alertToast } = useToast();
  const { data, isLoading } = useGetAdminMcpConnectionUrlQuery();
  const [generateUrl, { isLoading: isGenerating }] =
    useGenerateAdminMcpConnectionUrlMutation();

  const handleGenerate = async () => {
    try {
      await generateUrl().unwrap();
    } catch {
      alertToast({ message: "Failed to generate connection URL" });
    }
  };

  if (isLoading) {
    return <Skeleton className="t-h-7 t-w-full" />;
  }

  if (data?.generated && data?.mcp_url) {
    return <CopyUrlButton url={data.mcp_url} />;
  }

  return (
    <div>
      <Button
        customType="primary"
        size="small"
        onClick={handleGenerate}
        isLoading={isGenerating}
        disabled={isGenerating}
      >
        Generate Connection URL
      </Button>
    </div>
  );
};

const Steps = ({ isAdmin }: { isAdmin: boolean }) => {
  const firstStepDescription = isAdmin ? (
    <AdminConnectionUrl />
  ) : (
    <CustomerConnectionUrl />
  );

  const lastStepDescription = isAdmin
    ? "Click Connect. Claude will now be able to browse the Form 1120 documents your team has access to and review them."
    : "Click Connect. Claude will now have access to your Inkle financial data.";

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-text-body-sm t-font-semibold t-text-text-100">
        Set up in a few steps:
      </div>
      <div className="t-flex t-flex-col t-gap-6">
        <Step
          number={1}
          title="Generate & use this unique connection URL to connect claude"
          description={firstStepDescription}
        />
        <Step
          number={2}
          title="Open Claude Desktop"
          description="Launch the Claude Desktop app and go to Settings → Connectors."
        />
        <Step
          number={3}
          title="Add a custom connector"
          description='Click "Manage Connectors" then "Add Custom Connector".'
        />
        <Step
          number={4}
          title="Enter connector details"
          description='Provide a name (e.g. "Inkle") and paste the connection URL above.'
        />
        <Step number={5} title="Connect" description={lastStepDescription} />
      </div>
    </div>
  );
};

export const ClaudeIntegration = () => {
  usePageTitle("Claude Integration");
  const {
    isAdmin,
    isCpaInkleServiceAdmin,
    isInkleSuperAdmin,
    isServiceSuperAdmin,
    isAnyServiceSuperAdmin,
  } = useRoleBasedView();
  const isServiceTeamAdmin = Boolean(
    isAdmin ||
      isCpaInkleServiceAdmin ||
      isInkleSuperAdmin ||
      isServiceSuperAdmin ||
      isAnyServiceSuperAdmin
  );

  const description = isServiceTeamAdmin
    ? "Connect Claude to the Inkle admin MCP to browse and review Form 1120 documents for the entities your team supports."
    : "Connect your Inkle account to Claude using the Inkle's Model Context Protocol (MCP) to query your financial data directly from Claude.";

  return (
    <DashboardLayout header={<Header v2 title="Connect to Claude" />}>
      <div className="t-w-[680px] t-max-w-full t-flex t-flex-col t-gap-6">
        <div className="t-text-body-sm t-text-text-30">{description}</div>

        <div className="t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-white t-p-6 sm:t-p-4">
          <div className="t-flex t-flex-col t-gap-6">
            <Steps isAdmin={isServiceTeamAdmin} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
